import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Magnetic } from "@/components/Magnetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const channels = [
  { icon: Phone, label: "Call Us", value: "04 885 8977", href: "tel:+97148858977", testid: "contact-phone-link" },
  { icon: MessageCircle, label: "WhatsApp", value: "052 929 2511", href: "https://wa.me/971529292511", testid: "contact-whatsapp-link" },
  { icon: Mail, label: "Email", value: "arun@rmefandbeqpt.com", href: "mailto:arun@rmefandbeqpt.com", testid: "contact-email-link" },
  { icon: MapPin, label: "Visit Us", value: "DIP Phase 2, Off Expo 2020 Road, Dubai, UAE", href: "https://maps.google.com/?q=Dubai+Investment+Park+Phase+2,+Dubai,+UAE", testid: "contact-location-link" },
];

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors duration-300 focus:border-gold/60 focus:bg-white/[0.06]";

const Embers = () => {
  const embers = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        left: `${(i * 37 + 11) % 100}%`,
        size: 2 + (i % 4),
        delay: (i * 0.9) % 9,
        dur: 7 + (i % 6),
        drift: `${(i % 2 ? 1 : -1) * (14 + (i % 5) * 10)}px`,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {embers.map((e, i) => (
        <span
          key={i}
          className="ember"
          style={{
            left: e.left,
            width: e.size,
            height: e.size,
            animationDelay: `${e.delay}s`,
            animationDuration: `${e.dur}s`,
            "--drift": e.drift,
          }}
        />
      ))}
    </div>
  );
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", brand: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Thank you! Our team will reach out shortly.");
      setForm({ name: "", brand: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong — please call or WhatsApp us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden py-24 md:py-32" data-testid="contact-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(11,61,46,0.5),transparent_60%)]" aria-hidden="true" />
      <Embers />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 md:px-8 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold" data-testid="contact-eyebrow">Partner With Us</p>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-white md:text-5xl" data-testid="contact-heading">
              Book your <span className="italic text-gold">walkthrough.</span>
            </h2>
            <p className="mt-4 font-script text-3xl text-gold-light md:text-4xl">tell us about your brand</p>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-white/65 md:text-base">
              Share your concept and we'll arrange a private tour of our Dubai Investment Park facility.
              Every channel below is tappable — reach us however you like.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {channels.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={c.testid}
                  className="group flex h-full flex-col gap-3 rounded-xl border border-gold/20 bg-black/60 p-5 backdrop-blur-sm transition-all duration-500 hover:border-gold/50 hover:bg-gold/[0.06]"
                >
                  <c.icon size={20} strokeWidth={1.5} className="text-gold" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.26em] text-white/50">{c.label}</p>
                    <p className="mt-1 break-words text-sm font-medium text-white transition-colors duration-300 group-hover:text-gold-light">{c.value}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="rounded-2xl border border-gold/25 bg-forest-deep/60 p-7 backdrop-blur-sm md:p-10" data-testid="contact-form">
            <p className="font-serif text-2xl font-medium text-white">Book your kitchen tour</p>
            <p className="mt-1 text-xs font-light text-white/50">We reply within one business day.</p>
            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              <input required placeholder="Your name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
              <input placeholder="Brand name" value={form.brand} onChange={set("brand")} className={inputCls} data-testid="contact-brand-input" />
              <input required type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
              <input type="tel" placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
              <textarea
                rows={4}
                placeholder="Tell us about your concept, cuisine and timeline…"
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none sm:col-span-2`}
                data-testid="contact-message-input"
              />
            </div>
            <Magnetic strength={0.2} className="mt-7 w-full">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-gold-light disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Enquiry"} <Send size={14} />
              </button>
            </Magnetic>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
